import { useState } from 'react';
import { useRevalidator } from 'react-router';

import { Badge, Button, Card, Icon } from '@ds/core';
import { DataTable } from '@ds/data';
import type { DataTableColumn } from '@ds/data';
import { Modal } from '@ds/feedback';
import { FormField, Input, Switch } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { useAlteracaoPendente } from '@/comuns/useAlteracaoPendente';
import { dados } from '@/data/porta';
import type { SalaDaClinica } from '@/data/tipos';

/**
 * Salas de atendimento.
 *
 * Sala não se apaga, se desativa: a agenda de junho aponta para ela, e excluir
 * deixaria consulta antiga sem lugar. Desativada, some da grade e continua no
 * histórico.
 */
export function SalasDaClinica({ salas }: { salas: SalaDaClinica[] }) {
    const revalidator = useRevalidator();
    const aviso = useAviso();

    const [emEdicao, setEmEdicao] = useState<SalaDaClinica | 'nova' | null>(null);
    const [nome, setNome] = useState('');
    const [ativa, setAtiva] = useState(true);
    const [salvando, setSalvando] = useState(false);
    const formSujo = useAlteracaoPendente({ nome, ativa }, emEdicao);

    const abrir = (sala: SalaDaClinica | 'nova') => {
        setEmEdicao(sala);
        setNome(sala === 'nova' ? '' : sala.nome);
        setAtiva(sala === 'nova' ? true : sala.ativa);
    };

    const salvar = async () => {
        if (!emEdicao) return;
        setSalvando(true);
        try {
            await dados().salvarSala(emEdicao === 'nova' ? null : emEdicao.id, { nome, ativa });
            aviso({ title: emEdicao === 'nova' ? 'Sala criada' : 'Sala salva', description: nome });
            setEmEdicao(null);
            revalidator.revalidate();
        } catch (e) {
            aviso({
                title: 'Não foi possível salvar a sala',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setSalvando(false);
        }
    };

    const colunas: DataTableColumn<SalaDaClinica>[] = [
        {
            key: 'nome', header: 'Sala',
            render: (s) => <strong style={{ fontWeight: 600 }}>{s.nome}</strong>,
        },
        {
            key: 'ativa', header: 'Situação', width: 140,
            render: (s) => <Badge tone={s.ativa ? 'success' : 'neutral'}>{s.ativa ? 'Ativa' : 'Desativada'}</Badge>,
        },
        {
            key: 'acoes', header: '', width: 110, align: 'right',
            render: (s) => <Button size="sm" variant="secondary" onClick={() => abrir(s)}>Editar</Button>,
        },
    ];

    return (
        <>
            <Card
                title="Salas de atendimento"
                subtitle="Só a sala ativa aparece na grade da agenda."
                padding={0}
                actions={(
                    <Button size="sm" iconLeft={<Icon name="plus" size={14} />} onClick={() => abrir('nova')}>
                        Nova sala
                    </Button>
                )}
            >
                <DataTable
                    columns={colunas} rows={salas} rowKey="id"
                    stickyHeader={false} style={{ border: 0, borderRadius: 0 }}
                    emptyMessage="Nenhuma sala cadastrada."
                />
            </Card>

            <Modal
                open={emEdicao !== null}
                title={emEdicao === 'nova' ? 'Nova sala' : 'Editar sala'}
                onClose={() => setEmEdicao(null)}
                dirty={formSujo}
                footer={(
                    <>
                        <Button variant="ghost" onClick={() => setEmEdicao(null)}>Cancelar</Button>
                        <Button onClick={salvar} disabled={!nome.trim() || salvando}>
                            {salvando ? 'Salvando…' : 'Salvar'}
                        </Button>
                    </>
                )}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    <FormField label="Nome" htmlFor="sala-nome" required>
                        <Input id="sala-nome" value={nome} onChange={setNome} placeholder="Sala 3 — Laser" />
                    </FormField>
                    <Switch
                        checked={ativa} onChange={setAtiva} label="Ativa"
                        description="Desativada, a sala sai da grade da agenda e continua no histórico."
                    />
                </div>
            </Modal>
        </>
    );
}
